<script setup lang="ts">
import { ElForm, ElFormItem, ElSelect, ElOption } from "element-plus";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useGetVisitorCategories } from "@/api/visitorCategories";
import { STATUS } from "@/stores/constants";
import { useLocationSelector } from "@/composables/location";

interface Props {
  categoryFilter: string;
  statusFilter: string;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: "update:categoryFilter", value: string): void;
  (e: "update:statusFilter", value: string): void;
}>();

const { t } = useI18n();

const statuses = computed(() => [
  { label: t("statusFilters.all"), value: STATUS.ALL },
  { label: t("statusFilters.signedIn"), value: STATUS.IN },
  { label: t("statusFilters.signedOut"), value: STATUS.OUT },
  { label: t("statusFilters.pending"), value: STATUS.PENDING },
  { label: t("statusFilters.denied"), value: STATUS.DENIED },
]);

const { data: visitorCategories, isLoading: isCategoriesLoading } =
  useGetVisitorCategories();
const { isAllLocationsSelected, isLocationsLoaded } = useLocationSelector();

const categoriesLoading = computed(
  () => isCategoriesLoading.value || !isLocationsLoaded.value
);

const categoryFilterOptions = computed(() => {
  const defaultCategoryFilterOptions = {
    label: t("statusFilters.all"),
    value: STATUS.ALL,
  };

  if (!visitorCategories.value) return [defaultCategoryFilterOptions];

  const mappedCategoryFilterOptions = visitorCategories.value
    .filter(
      (category) => isAllLocationsSelected.value || category.settings.enabled
    )
    .map(({ name }) => ({
      label: name,
      value: name,
    }));

  return [defaultCategoryFilterOptions, ...mappedCategoryFilterOptions];
});
</script>

<template>
  <el-form
    label-position="top"
    class="filters-form"
  >
    <el-form-item
      :label="t('visitors.timeline.actions.filters.status')"
      class="mb-0"
    >
      <el-select
        :model-value="statusFilter"
        @change="emit('update:statusFilter', $event)"
      >
        <el-option
          v-for="statusOption in statuses"
          :key="statusOption.value"
          :value="statusOption.value"
          :label="statusOption.label"
        />
      </el-select>
    </el-form-item>

    <el-form-item
      :label="t('visitors.timeline.actions.filters.category')"
      class="mb-0"
    >
      <el-select
        :loading="categoriesLoading"
        :model-value="categoryFilter"
        @change="emit('update:categoryFilter', $event)"
      >
        <el-option
          v-for="option in categoryFilterOptions"
          :key="option.value"
          :value="option.value"
          :label="option.label"
        />
      </el-select>
    </el-form-item>
  </el-form>
</template>

<style lang="scss" scoped>
.filters-form {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}
</style>
